// src/theme/index.js (UPDATED WITH DESIGN SYSTEM)
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { darkThemeColors, lightThemeColors, colorTokens, colorPalettes, generateThemeColors } from './colors';
import { createComponentOverrides, componentTokens } from './components';
import { typography, typographyTokens } from './typography';
import { createExtendedTokens, createStyledComponents } from './enhancements';

// Import new design system tokens
import { colorTokens as designColorTokens, spacingTokens, typographyTokens as designTypographyTokens, shadowTokens, borderTokens, transitionTokens } from '../design-system/tokens';

// Central theme tokens
const themeTokens = {
  // Include component tokens directly
  ...componentTokens,

  // Component styles
  cardStyles: {
    standard: {
      borderRadius: componentTokens.borderRadius.card,
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
      transition: componentTokens.transitions.standard,
      '&:hover': {
        transform: 'translateY(-6px)',
      },
    },
    magical: {
      animation: componentTokens.animations.glow,
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 0 10px rgba(228, 180, 76, 0.4)',
      position: 'relative',
      '&:hover': {
        transform: 'translateY(-6px)',
      },
    },
    floating: {
      animation: componentTokens.animations.float,
    },
  },

  // Button styles
  buttonStyles: {
    sizes: {
      small: {
        padding: '0.5rem 1rem',
        fontSize: '0.75rem',
      },
      medium: {
        padding: '0.625rem 1.5rem',
        fontSize: '0.875rem',
      },
      large: {
        padding: '0.75rem 2rem',
        fontSize: '1rem',
      },
    },
  },

  // Text field styles
  textFieldStyles: {
    sizes: {
      small: {
        padding: '8px 12px',
        fontSize: '0.875rem',
      },
      medium: {
        padding: '10px 14px',
        fontSize: '0.875rem',
      },
      large: {
        padding: '12px 16px',
        fontSize: '1rem',
      },
    },
  },
};

/**
 * Creates the Aghrba theme with magical UI elements and effects
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @param {string} direction - Text direction ('ltr' or 'rtl')
 * @param {string} colorPalette - Color palette key (e.g., 'oceanProfessional', 'midnightGold')
 * @returns {Object} MUI theme object
 */
const createAghrbaTheme = (mode = 'dark', direction = 'ltr', colorPalette = 'original') => {
  // Get the selected palette or fall back to default
  const selectedPalette = colorPalettes[colorPalette] || colorPalettes.original;
  
  // Generate colors based on selected palette and theme mode
  const colors = generateThemeColors(selectedPalette, mode);
  
  // Create base theme with colors, typography, and custom properties
  let baseTheme = createTheme({
    direction,
    palette: {
      mode,
      primary: colors.primary,
      secondary: colors.secondary,
      background: colors.background,
      text: colors.text,
      error: colors.error,
      warning: colors.warning,
      info: colors.info,
      success: colors.success,
      action: colors.action,
    },
    typography,
    shape: {
      borderRadius: 8, // Base border radius
    },
    transitions: {
      easing: {
        magical: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        appear: 'cubic-bezier(0, 0, 0.2, 1)',
        disappear: 'cubic-bezier(0.4, 0, 1, 1)',
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
        magical: 450,
      },
    },
    // Aghrba custom theme extensions
    aghrba: {
      // Color tokens
      colors: colorTokens,

      // Typography tokens
      typography: typographyTokens,

      // Component tokens - FIXED
      tokens: themeTokens,

      // Magical effects
      effects: {
        inputFocus: colorTokens.effects.inputFocus(colors.secondary.main),
        lampGlow: mode === 'dark'
          ? `0 0 20px 5px ${colors.secondary.main}80`
          : `0 0 15px 5px ${colors.secondary.main}60`,
        cardGlow: mode === 'dark'
          ? `0 8px 16px rgba(0, 0, 0, 0.2), 0 0 10px ${colors.secondary.main}40, 0 0 30px ${colors.secondary.main}20`
          : `0 8px 16px rgba(0, 0, 0, 0.08), 0 0 10px ${colors.secondary.main}30, 0 0 30px ${colors.secondary.main}10`,
        magicSparkle: mode === 'dark'
          ? `0 0 8px 2px ${colors.secondary.main}80`
          : `0 0 8px 2px ${colors.secondary.main}60`,
      },

      // Gradients
      gradients: {
        goldText: colorTokens.gradients.goldText(colors),
        primaryButton: colorTokens.gradients.primaryButton(colors),
        secondaryButton: colorTokens.gradients.secondaryButton(colors),
        cardBorder: colorTokens.gradients.cardBorder(colors),
        pageBackground: colorTokens.gradients.pageBackground(colors),
      },

      // Animations
      animations: {
        float: componentTokens.animations.float,
        glow: componentTokens.animations.glow,
        shine: componentTokens.animations.shine,
        gradient: componentTokens.animations.gradient,
      },
    },
    shadows: [
      'none',
      '0 2px 8px rgba(0, 0, 0, 0.06)',
      '0 4px 12px rgba(0, 0, 0, 0.08)',
      '0 8px 16px rgba(0, 0, 0, 0.1)',
      '0 12px 24px rgba(0, 0, 0, 0.12)',
      '0 16px 32px rgba(0, 0, 0, 0.15)',
      ...Array(19).fill('0 20px 40px rgba(0, 0, 0, 0.2)'),
    ],
  });

  // Add extended tokens (augmenting the theme.aghrba.tokens)
  baseTheme.aghrba.tokens = createExtendedTokens(baseTheme);

  // Merge design system tokens
  baseTheme.aghrba.designSystem = {
    colors: designColorTokens,
    spacing: spacingTokens,
    typography: designTypographyTokens,
    shadows: shadowTokens,
    borders: borderTokens,
    transitions: transitionTokens
  };

  // Create component overrides with enhanced icon visibility fixes
  const componentOverrides = createComponentOverrides(baseTheme);

  // Create enhanced styled components
  const styledComponents = createStyledComponents(baseTheme);

  // Merge both sets of component styles
  const mergedComponents = {
    ...componentOverrides,
    ...styledComponents,
  };

  // Add component overrides
  const themeWithComponents = createTheme(baseTheme, {
    components: mergedComponents
  });
  
  // Apply responsive font sizes
  return responsiveFontSizes(themeWithComponents);
};

export default createAghrbaTheme;